import React, { useState, useEffect, useCallback } from 'react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { authFetch } from '../services/api';

const COLORS = ['#6366f1', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#14b8a6'];

export default function AdminAnalytics({ onBack }) {
  const [days, setDays] = useState(30);
  const [overview, setOverview] = useState(null);
  const [projects, setProjects] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [costs, setCosts] = useState([]);
  const [deployments, setDeployments] = useState(null);
  const [stacks, setStacks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const endpoints = [
        '/api/admin/analytics/overview',
        `/api/admin/analytics/projects?days=${days}`,
        `/api/admin/analytics/tokens?days=${days}`,
        `/api/admin/analytics/costs?days=${days}`,
        '/api/admin/analytics/deployments',
        '/api/admin/analytics/stacks',
        `/api/admin/analytics/users?days=${days}`,
      ];
      const results = await Promise.all(
        endpoints.map(ep => authFetch(ep).then(r => r.ok ? r.json() : null).catch(() => null))
      );
      setOverview(results[0]);
      setProjects(results[1] || []);
      setTokens(results[2] || []);
      setCosts(results[3] || []);
      setDeployments(results[4]);
      setStacks(results[5] || []);
      setUsers(results[6] || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [days]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const statusPieData = overview ? [
    { name: 'Completed', value: overview.completed },
    { name: 'Failed', value: overview.failed },
    { name: 'Other', value: (overview.total_jobs || 0) - overview.completed - overview.failed },
  ].filter(d => d.value > 0) : [];

  const deployPieData = deployments ? [
    { name: 'Success', value: deployments.successful },
    { name: 'Failed', value: deployments.failed },
  ].filter(d => d.value > 0) : [];

  // Extract unique model keys from token data
  const modelKeys = [...new Set(tokens.flatMap(d => Object.keys(d).filter(k => k !== 'date')))];

  const isDark = document.documentElement.getAttribute('data-theme') !== 'light';
  const gridStroke = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)';
  const tickFill = isDark ? '#94a3b8' : '#64748b';
  const tooltipStyle = isDark
    ? { background: '#0e1128', border: '1px solid rgba(99,102,241,0.25)', borderRadius: 10, fontSize: 12, color: '#e2e8f0', boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }
    : { background: '#ffffff', border: '1px solid rgba(99,102,241,0.18)', borderRadius: 10, fontSize: 12, color: '#0f172a', boxShadow: '0 4px 16px rgba(99,102,241,0.1)' };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingText}>Loading analytics...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <style>{AA_CSS}</style>
      <div style={styles.headerRow}>
        {onBack && <button onClick={onBack} style={styles.backBtn}>← Back</button>}
        <h1 style={styles.title}>Platform Analytics</h1>
        <select value={days} onChange={e => setDays(Number(e.target.value))} style={styles.select}>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last year</option>
        </select>
      </div>

      {/* Overview Cards */}
      {overview && (
        <div style={styles.cardGrid}>
          <MetricCard label="Total Projects" value={overview.total_projects} />
          <MetricCard label="Total Tokens" value={formatNumber(overview.total_tokens)} />
          <MetricCard label="Total Cost" value={`$${overview.total_cost_usd}`} />
          <MetricCard label="Total Users" value={overview.total_users} />
          <MetricCard label="Success Rate" value={`${overview.success_rate}%`} />
        </div>
      )}

      <div style={styles.chartsGrid}>
        {/* Projects Over Time */}
        <ChartCard title="Projects Over Time" subtitle={`${projects.length} data points`}>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={projects} barCategoryGap="32%" barGap={2} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <defs>
                {/* Completed — refined violet/indigo */}
                <linearGradient id="potDone" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor={isDark ? '#818cf8' : '#6366f1'} stopOpacity={isDark ? 0.95 : 0.9} />
                  <stop offset="100%" stopColor={isDark ? '#6366f1' : '#4f46e5'} stopOpacity={isDark ? 0.8 : 1} />
                </linearGradient>
                {/* Failed — muted rose/crimson */}
                <linearGradient id="potFailed" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor={isDark ? '#f87171' : '#e05c6e'} stopOpacity={isDark ? 0.9 : 0.85} />
                  <stop offset="100%" stopColor={isDark ? '#dc2626' : '#be123c'} stopOpacity={isDark ? 0.75 : 0.95} />
                </linearGradient>
                {/* Running — subtle amber */}
                <linearGradient id="potRunning" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor={isDark ? '#fbbf24' : '#d97706'} stopOpacity={isDark ? 0.9 : 0.85} />
                  <stop offset="100%" stopColor={isDark ? '#b45309' : '#92400e'} stopOpacity={isDark ? 0.75 : 0.95} />
                </linearGradient>
                {/* Pending — soft blue */}
                <linearGradient id="potPending" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor={isDark ? '#60a5fa' : '#3b82f6'} stopOpacity={isDark ? 0.9 : 0.85} />
                  <stop offset="100%" stopColor={isDark ? '#2563eb' : '#1d4ed8'} stopOpacity={isDark ? 0.75 : 0.95} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="0"
                stroke={isDark ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.06)'}
                vertical={false}
              />
              <XAxis
                dataKey="date"
                tickFormatter={shortDate}
                tick={{ fill: tickFill, fontSize: 11, fontWeight: 500 }}
                axisLine={{ stroke: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.1)' }}
                tickLine={false}
                dy={6}
              />
              <YAxis
                tick={{ fill: tickFill, fontSize: 11, fontWeight: 500 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={v => v === 0 ? '' : v}
                width={28}
                dx={-4}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                cursor={{ fill: isDark ? 'rgba(99,102,241,0.06)' : 'rgba(99,102,241,0.04)', radius: 6 }}
                labelFormatter={d => d}
              />
              <Legend
                iconType="circle"
                iconSize={7}
                wrapperStyle={{ paddingTop: 16, fontSize: 11, fontWeight: 600, letterSpacing: '0.03em', color: tickFill }}
              />
              <Bar dataKey="done" stackId="a" fill="url(#potDone)" name="Completed" radius={[0, 0, 0, 0]} />
              <Bar dataKey="failed" stackId="a" fill="url(#potFailed)" name="Failed" radius={[0, 0, 0, 0]} />
              <Bar dataKey="running" stackId="a" fill="url(#potRunning)" name="Running" radius={[0, 0, 0, 0]} />
              <Bar dataKey="pending" stackId="a" fill="url(#potPending)" name="Pending" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Job Status Distribution */}
        <ChartCard title="Job Status Distribution" subtitle={overview ? `${overview.total_jobs || 0} total jobs` : undefined}>
          <div className="aa-jsd-wrap">
            <ResponsiveContainer width="100%" height={220}>
              <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                <defs>
                  <filter id="jsdGlow">
                    <feGaussianBlur stdDeviation="3" result="blur" />
                    <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                  </filter>
                </defs>
                <Pie
                  data={statusPieData}
                  cx="50%" cy="50%"
                  innerRadius={62} outerRadius={88}
                  dataKey="value"
                  paddingAngle={3}
                  strokeWidth={0}
                  label={false}
                  labelLine={false}
                >
                  {statusPieData.map((_, i) => (
                    <Cell
                      key={i}
                      fill={[
                        isDark ? '#818cf8' : '#6366f1',
                        isDark ? '#f87171' : '#e05c6e',
                        isDark ? '#60a5fa' : '#3b82f6',
                      ][i]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(value, name) => [value, name]}
                />
              </PieChart>
            </ResponsiveContainer>
            {/* centre label */}
            <div className="aa-jsd-centre">
              <span className="aa-jsd-centre-value">{overview ? overview.total_jobs || 0 : ''}</span>
              <span className="aa-jsd-centre-label">Total</span>
            </div>
            {/* custom legend */}
            <div className="aa-jsd-legend">
              {statusPieData.map((entry, i) => {
                const colors = [
                  isDark ? '#818cf8' : '#6366f1',
                  isDark ? '#f87171' : '#e05c6e',
                  isDark ? '#60a5fa' : '#3b82f6',
                ];
                const total = statusPieData.reduce((s, d) => s + d.value, 0);
                const pct = total > 0 ? ((entry.value / total) * 100).toFixed(1) : '0.0';
                return (
                  <div key={entry.name} className="aa-jsd-legend-row">
                    <span className="aa-jsd-legend-dot" style={{ background: colors[i] }} />
                    <span className="aa-jsd-legend-name">{entry.name}</span>
                    <span className="aa-jsd-legend-pct">{pct}%</span>
                    <span className="aa-jsd-legend-val">{entry.value}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </ChartCard>

        {/* Token Consumption */}
        <ChartCard title="Token Consumption by Model" subtitle={`${modelKeys.length} model${modelKeys.length !== 1 ? 's' : ''}`}>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={tokens} barCategoryGap="32%" barGap={2} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="0" stroke={isDark ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.06)'} vertical={false} />
              <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fill: tickFill, fontSize: 11, fontWeight: 500 }} axisLine={{ stroke: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.1)' }} tickLine={false} dy={6} />
              <YAxis tick={{ fill: tickFill, fontSize: 11, fontWeight: 500 }} axisLine={false} tickLine={false} tickFormatter={v => v === 0 ? '' : formatNumber(v)} width={36} dx={-4} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: isDark ? 'rgba(99,102,241,0.06)' : 'rgba(99,102,241,0.04)', radius: 6 }} labelFormatter={d => d} />
              <Legend iconType="circle" iconSize={7} wrapperStyle={{ paddingTop: 16, fontSize: 11, fontWeight: 600, letterSpacing: '0.03em', color: tickFill }} />
              {modelKeys.map((key, i) => (
                <Bar key={key} dataKey={key} fill={COLORS[i % COLORS.length]} name={key} radius={i === modelKeys.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Daily API Costs */}
        <ChartCard title="Daily API Costs ($)" subtitle={`${costs.length} data points`}>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={costs} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="costArea" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#8b5cf6" stopOpacity={isDark ? 0.18 : 0.1} />
                  <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="0" stroke={isDark ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.06)'} vertical={false} />
              <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fill: tickFill, fontSize: 11, fontWeight: 500 }} axisLine={{ stroke: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.1)' }} tickLine={false} dy={6} />
              <YAxis tick={{ fill: tickFill, fontSize: 11, fontWeight: 500 }} axisLine={false} tickLine={false} tickFormatter={v => v === 0 ? '' : `$${v}`} width={36} dx={-4} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ stroke: isDark ? 'rgba(139,92,246,0.3)' : 'rgba(139,92,246,0.2)', strokeWidth: 1, strokeDasharray: '4 2' }} formatter={v => [`$${v}`, 'Cost']} labelFormatter={d => d} />
              <Line type="monotone" dataKey="cost_usd" stroke={isDark ? '#a78bfa' : '#7c3aed'} strokeWidth={2.5} dot={false} name="Cost" activeDot={{ r: 4, fill: isDark ? '#a78bfa' : '#7c3aed', strokeWidth: 0 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Deployment Success */}
        <ChartCard title="Deployment Success Rate" subtitle={deployments ? `${deployments.total_attempted || 0} total` : undefined}>
          {deployments && deployments.total_attempted > 0 ? (
            <div className="aa-jsd-wrap">
              <ResponsiveContainer width="100%" height={220}>
                <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                  <Pie data={deployPieData} cx="50%" cy="50%" innerRadius={62} outerRadius={88} dataKey="value" paddingAngle={3} strokeWidth={0} label={false} labelLine={false}>
                    {deployPieData.map((_, i) => (
                      <Cell key={i} fill={[isDark ? '#818cf8' : '#6366f1', isDark ? '#f87171' : '#e05c6e'][i]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} formatter={(value, name) => [value, name]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="aa-jsd-centre">
                <span className="aa-jsd-centre-value">{deployments.total_attempted}</span>
                <span className="aa-jsd-centre-label">Deploys</span>
              </div>
              <div className="aa-jsd-legend">
                {deployPieData.map((entry, i) => {
                  const colors = [isDark ? '#818cf8' : '#6366f1', isDark ? '#f87171' : '#e05c6e'];
                  const total = deployPieData.reduce((s, d) => s + d.value, 0);
                  const pct = total > 0 ? ((entry.value / total) * 100).toFixed(1) : '0.0';
                  return (
                    <div key={entry.name} className="aa-jsd-legend-row">
                      <span className="aa-jsd-legend-dot" style={{ background: colors[i] }} />
                      <span className="aa-jsd-legend-name">{entry.name}</span>
                      <span className="aa-jsd-legend-pct">{pct}%</span>
                      <span className="aa-jsd-legend-val">{entry.value}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div style={styles.emptyState}>No deployments yet</div>
          )}
        </ChartCard>

        {/* Popular Tech Stacks */}
        <ChartCard title="Popular Tech Stacks" subtitle="Top 10">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={stacks.slice(0, 10)} layout="vertical" margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="0" stroke={isDark ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.06)'} horizontal={false} />
              <XAxis type="number" tick={{ fill: tickFill, fontSize: 11, fontWeight: 500 }} axisLine={{ stroke: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.1)' }} tickLine={false} tickFormatter={v => v === 0 ? '' : v} dy={4} />
              <YAxis type="category" dataKey="name" tick={{ fill: tickFill, fontSize: 11, fontWeight: 500 }} axisLine={false} tickLine={false} width={100} dx={-4} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: isDark ? 'rgba(99,102,241,0.06)' : 'rgba(99,102,241,0.04)', radius: 4 }} />
              <Bar dataKey="count" fill={isDark ? '#818cf8' : '#6366f1'} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* User Activity Trends */}
        <ChartCard title="User Activity Trends" subtitle={`${users.length} data points`}>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={users} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="0" stroke={isDark ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.06)'} vertical={false} />
              <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fill: tickFill, fontSize: 11, fontWeight: 500 }} axisLine={{ stroke: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.1)' }} tickLine={false} dy={6} />
              <YAxis tick={{ fill: tickFill, fontSize: 11, fontWeight: 500 }} axisLine={false} tickLine={false} tickFormatter={v => v === 0 ? '' : v} width={28} dx={-4} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ stroke: isDark ? 'rgba(99,102,241,0.25)' : 'rgba(99,102,241,0.15)', strokeWidth: 1, strokeDasharray: '4 2' }} labelFormatter={d => d} />
              <Legend iconType="circle" iconSize={7} wrapperStyle={{ paddingTop: 16, fontSize: 11, fontWeight: 600, letterSpacing: '0.03em', color: tickFill }} />
              <Line type="monotone" dataKey="signups" stroke={isDark ? '#22d3ee' : '#0891b2'} strokeWidth={2.5} dot={false} name="Signups" activeDot={{ r: 4, strokeWidth: 0 }} />
              <Line type="monotone" dataKey="active_users" stroke={isDark ? '#a78bfa' : '#7c3aed'} strokeWidth={2.5} dot={false} name="Active Users" activeDot={{ r: 4, strokeWidth: 0 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}

function MetricCard({ label, value }) {
  return (
    <div className="aa-metric-card">
      <div className="aa-metric-value">{value}</div>
      <div className="aa-metric-label">{label}</div>
      <div className="aa-metric-bar" />
    </div>
  );
}

function ChartCard({ title, subtitle, children }) {
  return (
    <div className="aa-chart-card">
      <div className="aa-chart-header">
        <span className="aa-chart-dot" />
        <h3 className="aa-chart-title">{title}</h3>
        {subtitle && <span className="aa-chart-subtitle">{subtitle}</span>}
      </div>
      <div className="aa-chart-body">{children}</div>
    </div>
  );
}

function shortDate(d) {
  if (!d) return '';
  const parts = d.split('-');
  return parts.length === 3 ? `${parts[1]}/${parts[2]}` : d;
}

function formatNumber(n) {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
}

const styles = {
  container: { padding: '32px 0', maxWidth: 1100, margin: '0 auto' },
  loadingText: { textAlign: 'center', color: 'var(--text-muted)', padding: 60, fontSize: 14 },
  headerRow: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 32, flexWrap: 'wrap' },
  title: { fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', margin: 0, flex: 1 },
  backBtn: {
    padding: '8px 14px', background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.3)',
    borderRadius: 8, color: 'var(--accent-light)', cursor: 'pointer', fontSize: 13, fontWeight: 600,
  },
  select: {
    padding: '8px 14px', background: 'var(--bg-input)', border: '1px solid var(--border)',
    borderRadius: 8, color: 'var(--text-primary)', fontSize: 13, cursor: 'pointer',
  },
  cardGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 16, marginBottom: 32,
  },
  chartsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 480px), 1fr))', gap: 20 },
  tooltip: {
    background: '#0e1128', border: '1px solid rgba(99,102,241,0.25)',
    borderRadius: 10, fontSize: 12, color: '#e2e8f0',
    boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
  },
  emptyState: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: 280, color: 'var(--text-muted)', fontSize: 13 },
};

const AA_CSS = `
@keyframes aa-bar-grow { from { transform: scaleX(0); } to { transform: scaleX(1); } }

/* ── Metric cards ── */
.aa-metric-card {
  position: relative;
  padding: 24px 20px 22px;
  border-radius: 16px;
  text-align: center;
  overflow: hidden;
  background: linear-gradient(155deg, #12152b 0%, #0c0f22 100%);
  border: 1px solid rgba(99,102,241,0.14);
  box-shadow: 0 1px 3px rgba(0,0,0,0.6), 0 6px 24px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.07);
  transition: transform 0.2s cubic-bezier(0.22,1,0.36,1), box-shadow 0.2s ease, border-color 0.2s ease;
}
.aa-metric-card::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px;
  background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 55%, #a78bfa 100%);
  border-radius: 16px 16px 0 0; opacity: 0.9;
}
.aa-metric-card::after {
  content: ''; position: absolute; top: 0; left: 0;
  width: 130px; height: 90px;
  background: radial-gradient(ellipse at 0% 0%, rgba(99,102,241,0.12) 0%, transparent 70%);
  pointer-events: none; border-radius: 16px 0 0 0;
}
.aa-metric-card:hover {
  transform: translateY(-2px);
  border-color: rgba(99,102,241,0.28);
  box-shadow: 0 2px 8px rgba(0,0,0,0.5), 0 12px 32px rgba(0,0,0,0.32), 0 0 0 1px rgba(99,102,241,0.12), inset 0 1px 0 rgba(255,255,255,0.09);
}
.aa-metric-value {
  font-size: 28px; font-weight: 800; letter-spacing: -0.04em; line-height: 1;
  background: linear-gradient(120deg, #f1f5f9 0%, #c7d2fe 45%, #a5b4fc 100%);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent;
  margin-bottom: 7px;
}
.aa-metric-label {
  font-size: 10px; font-weight: 600; letter-spacing: 0.07em; text-transform: uppercase;
  color: rgba(148,163,184,0.55);
}
.aa-metric-bar {
  position: absolute; bottom: 0; left: 0; right: 0; height: 2px;
  background: linear-gradient(90deg, rgba(99,102,241,0.5), rgba(139,92,246,0.25), transparent);
  transform-origin: left;
  animation: aa-bar-grow 0.7s cubic-bezier(0.22,1,0.36,1) both;
}

/* light theme — metric cards */
[data-theme="light"] .aa-metric-card {
  background: #ffffff;
  border-color: rgba(99,102,241,0.13);
  box-shadow: 0 1px 3px rgba(15,23,42,0.06), 0 6px 20px rgba(15,23,42,0.05);
}
[data-theme="light"] .aa-metric-card::before { opacity: 0.65; }
[data-theme="light"] .aa-metric-card::after {
  background: radial-gradient(ellipse at 0% 0%, rgba(99,102,241,0.05) 0%, transparent 70%);
}
[data-theme="light"] .aa-metric-card:hover {
  border-color: rgba(99,102,241,0.24);
  box-shadow: 0 2px 8px rgba(15,23,42,0.07), 0 10px 28px rgba(99,102,241,0.07), 0 0 0 1px rgba(99,102,241,0.08);
}
[data-theme="light"] .aa-metric-value {
  background: linear-gradient(120deg, #0f172a 0%, #3730a3 50%, #4f46e5 100%);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent;
}
[data-theme="light"] .aa-metric-label { color: rgba(71,85,105,0.65); }
[data-theme="light"] .aa-metric-bar { background: linear-gradient(90deg, rgba(99,102,241,0.28), rgba(139,92,246,0.12), transparent); }

/* ── Chart cards ── */
.aa-chart-card {
  position: relative;
  border-radius: 18px;
  overflow: hidden;
  background: linear-gradient(155deg, #12152b 0%, #0c0f22 100%);
  border: 1px solid rgba(99,102,241,0.13);
  box-shadow: 0 1px 3px rgba(0,0,0,0.6), 0 8px 32px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.06);
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}
.aa-chart-card::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px;
  background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 40%, rgba(139,92,246,0.2) 100%);
  border-radius: 18px 18px 0 0; opacity: 0.8;
}
.aa-chart-card:hover {
  border-color: rgba(99,102,241,0.24);
  box-shadow: 0 2px 8px rgba(0,0,0,0.5), 0 14px 40px rgba(0,0,0,0.32), 0 0 0 1px rgba(99,102,241,0.1), inset 0 1px 0 rgba(255,255,255,0.08);
}
.aa-chart-header {
  display: flex; align-items: center; gap: 9px;
  padding: 18px 22px 0;
  border-bottom: 1px solid rgba(255,255,255,0.04);
  padding-bottom: 14px;
}
.aa-chart-dot {
  width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0;
  background: linear-gradient(135deg, #818cf8, #a78bfa);
  box-shadow: 0 0 5px rgba(129,140,248,0.5);
}
.aa-chart-title {
  font-size: 11px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase;
  color: rgba(148,163,184,0.7); margin: 0;
}
.aa-chart-subtitle {
  margin-left: auto;
  font-size: 10px; font-weight: 500; letter-spacing: 0.03em;
  color: rgba(148,163,184,0.4);
  white-space: nowrap;
  background: rgba(99,102,241,0.08);
  border: 1px solid rgba(99,102,241,0.14);
  border-radius: 20px;
  padding: 2px 8px;
}
.aa-chart-body { padding: 18px 22px 22px; }

/* light theme — chart cards */
[data-theme="light"] .aa-chart-card {
  background: #ffffff;
  border-color: rgba(99,102,241,0.11);
  box-shadow: 0 1px 3px rgba(15,23,42,0.06), 0 6px 24px rgba(15,23,42,0.05);
}
[data-theme="light"] .aa-chart-card::before { opacity: 0.55; }
[data-theme="light"] .aa-chart-card:hover {
  border-color: rgba(99,102,241,0.2);
  box-shadow: 0 2px 8px rgba(15,23,42,0.07), 0 12px 32px rgba(99,102,241,0.06), 0 0 0 1px rgba(99,102,241,0.07);
}
[data-theme="light"] .aa-chart-header { border-bottom-color: rgba(15,23,42,0.06); }
[data-theme="light"] .aa-chart-title { color: rgba(51,65,85,0.75); }
[data-theme="light"] .aa-chart-subtitle {
  color: rgba(71,85,105,0.55);
  background: rgba(99,102,241,0.05);
  border-color: rgba(99,102,241,0.12);
}
[data-theme="light"] .aa-chart-dot {
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  box-shadow: 0 0 4px rgba(99,102,241,0.35);
}

/* ── Job Status Distribution + Deployment donut ── */
.aa-jsd-wrap {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
}
.aa-jsd-centre {
  position: absolute;
  top: 0; left: 50%;
  transform: translateX(-50%);
  width: 220px; height: 220px;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  pointer-events: none;
}
.aa-jsd-centre-value {
  font-size: 30px; font-weight: 800; letter-spacing: -0.04em; line-height: 1;
  background: linear-gradient(120deg, #f1f5f9 0%, #c7d2fe 45%, #a5b4fc 100%);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent;
}
.aa-jsd-centre-label {
  font-size: 10px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase;
  color: rgba(148,163,184,0.45); margin-top: 5px;
}
.aa-jsd-legend {
  width: 100%;
  display: flex; flex-direction: column; gap: 7px;
}
.aa-jsd-legend-row {
  display: flex; align-items: center; gap: 10px;
  padding: 9px 13px;
  border-radius: 10px;
  background: rgba(255,255,255,0.035);
  border: 1px solid rgba(99,102,241,0.1);
  transition: background 0.18s ease, border-color 0.18s ease;
}
.aa-jsd-legend-row:hover {
  background: rgba(99,102,241,0.08);
  border-color: rgba(99,102,241,0.2);
}
.aa-jsd-legend-dot {
  width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0;
  /* glow set via filter so it uses the background color, not currentColor */
  filter: brightness(1.2) drop-shadow(0 0 4px currentColor);
}
.aa-jsd-legend-name {
  flex: 1;
  font-size: 12px; font-weight: 600;
  color: rgba(226,232,240,0.85);
  letter-spacing: 0.01em;
}
.aa-jsd-legend-pct {
  font-size: 12px; font-weight: 700;
  color: rgba(165,180,252,0.95);
  letter-spacing: 0.02em; min-width: 42px; text-align: right;
}
.aa-jsd-legend-val {
  font-size: 11px; font-weight: 500;
  color: rgba(148,163,184,0.45);
  min-width: 30px; text-align: right;
  padding-left: 8px;
  border-left: 1px solid rgba(255,255,255,0.07);
}
/* light theme — donut legend */
[data-theme="light"] .aa-jsd-centre-value {
  background: linear-gradient(120deg, #0f172a 0%, #3730a3 50%, #4f46e5 100%);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent;
}
[data-theme="light"] .aa-jsd-centre-label { color: rgba(71,85,105,0.55); }
[data-theme="light"] .aa-jsd-legend-row {
  background: rgba(99,102,241,0.03);
  border-color: rgba(99,102,241,0.09);
}
[data-theme="light"] .aa-jsd-legend-row:hover {
  background: rgba(99,102,241,0.07);
  border-color: rgba(99,102,241,0.18);
}
[data-theme="light"] .aa-jsd-legend-name { color: #1e293b; }
[data-theme="light"] .aa-jsd-legend-pct { color: #4338ca; }
[data-theme="light"] .aa-jsd-legend-val {
  color: rgba(100,116,139,0.6);
  border-left-color: rgba(15,23,42,0.08);
}

/* ── Responsive ── */
@media (max-width: 600px) {
  .aa-chart-header { padding: 14px 16px 0; padding-bottom: 12px; }
  .aa-chart-body   { padding: 14px 14px 18px; }
  .aa-chart-title  { font-size: 10px; }
  .aa-chart-subtitle { display: none; }
  .aa-metric-value { font-size: 22px; }
  .aa-jsd-legend-val { display: none; }
}
`;
